// KP-3919: dependency-free STORE-mode (no compression) streaming ZIP writer
// for the client-zip download tier. Brand files are already-compressed
// formats (pdf/pptx/zip…), so store-mode costs nothing while letting the
// browser write a zip of ANY size straight to disk chunk by chunk.
//
// Format notes: entries stream with the data-descriptor flag (bit 3) since
// CRCs are only known after the bytes pass through; the archive is written
// in ZIP64 form whenever sizes are (or could be) large, which every modern
// unzip reads.

/* eslint-disable no-bitwise */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n += 1) {
    let c = n
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[n] = c >>> 0
  }
  return table
})()

export const crc32 = (crc, chunk) => {
  let c = crc ^ 0xffffffff
  for (let i = 0; i < chunk.length; i += 1) {
    c = CRC_TABLE[(c ^ chunk[i]) & 0xff] ^ (c >>> 8)
  }
  return (c ^ 0xffffffff) >>> 0
}

// Lazy: jsdom test environments lack TextEncoder at import time.
let encoder = null
const encodeName = value => {
  if (!encoder) encoder = new TextEncoder()
  return encoder.encode(value)
}

const u16 = value => new Uint8Array([value & 0xff, (value >>> 8) & 0xff])
const u32 = value => new Uint8Array([
  value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff,
])
// Two 32-bit halves — exact for any realistic size (< 2^53 bytes) without
// BigInt, which the legacy build toolchain cannot transpile.
const u64 = value => {
  const lo = value % 0x100000000
  const hi = Math.floor(value / 0x100000000)
  const out = new Uint8Array(8)
  out.set(u32(lo), 0)
  out.set(u32(hi), 4)
  return out
}

const concat = parts => {
  const total = parts.reduce((n, p) => n + p.length, 0)
  const out = new Uint8Array(total)
  let offset = 0
  parts.forEach(p => {
    out.set(p, offset)
    offset += p.length
  })
  return out
}

const ZIP64_VERSION = 45
const FLAG_DATA_DESCRIPTOR = 0x0008

/**
 * Streaming zip builder. Usage:
 *   const zip = new ZipStreamWriter(chunk => sink(chunk))
 *   await zip.startEntry('Folder/name.pdf')
 *   zip.pushData(uint8chunk) // repeat
 *   zip.endEntry()
 *   zip.finish()
 * Every emitted chunk goes to the sink immediately — nothing accumulates
 * except the (tiny) central directory records.
 */
export class ZipStreamWriter {
  constructor(sink) {
    this.sink = sink
    this.offset = 0
    this.entries = []
    this.current = null
  }

  write(bytes) {
    this.sink(bytes)
    this.offset += bytes.length
  }

  startEntry(name) {
    const nameBytes = encodeName(name)
    this.current = {
      name: nameBytes,
      offset: this.offset,
      crc: 0,
      size: 0,
    }
    // Local file header: sizes unknown (bit 3), zip64 markers.
    this.write(concat([
      u32(0x04034b50),
      u16(ZIP64_VERSION),
      u16(FLAG_DATA_DESCRIPTOR),
      u16(0), // method: store
      u32(0), // dos time/date: epoch — irrelevant for delivery zips
      u32(0), // crc (in descriptor)
      u32(0xffffffff), // compressed size -> zip64
      u32(0xffffffff), // uncompressed size -> zip64
      u16(nameBytes.length),
      u16(20), // extra length: zip64 placeholder
      nameBytes,
      // Zip64 extra with zeroed sizes (real values live in the descriptor).
      u16(0x0001), u16(16), u64(0), u64(0),
    ]))
  }

  pushData(chunk) {
    const bytes = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk)
    // crc32 takes/returns finalized values, so chaining chunk-by-chunk works.
    this.current.crc = crc32(this.current.crc, bytes)
    this.current.size += bytes.length
    this.write(bytes)
  }

  endEntry() {
    const entry = this.current
    // Zip64 data descriptor: crc + 8-byte sizes (store: comp == uncomp).
    this.write(concat([
      u32(0x08074b50),
      u32(entry.crc),
      u64(entry.size),
      u64(entry.size),
    ]))
    this.entries.push(entry)
    this.current = null
  }

  finish() {
    const centralStart = this.offset
    this.entries.forEach(entry => {
      this.write(concat([
        u32(0x02014b50),
        u16(ZIP64_VERSION), // made by
        u16(ZIP64_VERSION), // needed
        u16(FLAG_DATA_DESCRIPTOR),
        u16(0), // store
        u32(0),
        u32(entry.crc),
        u32(0xffffffff),
        u32(0xffffffff),
        u16(entry.name.length),
        u16(28), // zip64 extra: sizes + offset
        u16(0), // comment
        u16(0), // disk
        u16(0), // internal attrs
        u32(0), // external attrs
        u32(0xffffffff), // offset -> zip64
        entry.name,
        u16(0x0001), u16(24), u64(entry.size), u64(entry.size), u64(entry.offset),
      ]))
    })
    const centralSize = this.offset - centralStart
    // Zip64 end of central directory + locator + classic EOCD.
    const eocd64Offset = this.offset
    this.write(concat([
      u32(0x06064b50),
      u64(44),
      u16(ZIP64_VERSION), u16(ZIP64_VERSION),
      u32(0), u32(0),
      u64(this.entries.length), u64(this.entries.length),
      u64(centralSize), u64(centralStart),
    ]))
    this.write(concat([
      u32(0x07064b50),
      u32(0),
      u64(eocd64Offset),
      u32(1),
    ]))
    this.write(concat([
      u32(0x06054b50),
      u16(0), u16(0),
      u16(0xffff), u16(0xffff),
      u32(0xffffffff), u32(0xffffffff),
      u16(0),
    ]))
  }
}
